import { useState } from 'react'
import { Aircraft } from '../../types/intellijetnet'
import { ContainerType } from '../../types/database'
import { AircraftMovementService } from '../../services/aircraft-movement.service'
import { MovementValidationResult } from '../../utils/aircraft-movement-validation'
import AircraftMovementModal from './AircraftMovementModal'

interface AircraftMovementControlsProps {
  aircraft: Aircraft
  modelId: number
  currentContainer: ContainerType
  onUpdate: () => void
  className?: string
}

export default function AircraftMovementControls({
  aircraft,
  modelId: _modelId,
  currentContainer,
  onUpdate,
  className = ''
}: AircraftMovementControlsProps) {
  const [isValidating, setIsValidating] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [showMovementModal, setShowMovementModal] = useState(false)
  const [movementToContainer, setMovementToContainer] = useState<ContainerType | null>(null)
  const [movementValidation, setMovementValidation] = useState<MovementValidationResult | null>(null)
  const [isMoving, setIsMoving] = useState(false)

  const getAvailableTargetContainers = (): ContainerType[] => {
    switch (currentContainer) {
      case 'for_sale':
        return ['sold', 'removed_from_market']
      case 'sold':
        return ['for_sale', 'removed_from_market']
      case 'removed_from_market':
        return ['for_sale', 'sold']
      default:
        return []
    }
  }

  const getContainerDisplayName = (container: ContainerType): string => {
    switch (container) {
      case 'for_sale': return 'For Sale'
      case 'sold': return 'Sold'
      case 'removed_from_market': return 'Removed from Market'
      default: return container
    }
  }

  const getCurrentContainerDisplayName = (): string => {
    return getContainerDisplayName(currentContainer)
  }

  const handleMoveAircraft = async (toContainer: ContainerType) => {
    try {
      setIsValidating(true)
      setShowDropdown(false)
      
      // Validate the movement
      const validation = await AircraftMovementService.validateMovement(
        aircraft,
        currentContainer,
        toContainer
      )

      // Set up modal state
      setMovementToContainer(toContainer)
      setMovementValidation(validation)
      setShowMovementModal(true)
    } catch (error) {
      alert('Failed to validate movement. Please try again.')
    } finally {
      setIsValidating(false)
    }
  }

  const handleConfirmMovement = async (reason?: string, notes?: string) => {
    if (!movementToContainer) return

    try {
      setIsMoving(true)

      await AircraftMovementService.moveAircraft(
        aircraft,
        currentContainer,
        movementToContainer,
        {
          reason,
          notes,
          movedBy: 'current_user', // TODO: Get from auth context
          skipValidation: true // Already validated
        }
      )

      // Close modal
      setShowMovementModal(false)
      setMovementToContainer(null)
      setMovementValidation(null)

      // Show success message
      alert(`Successfully moved aircraft to ${getContainerDisplayName(movementToContainer)}`)

      // Trigger update callback to refresh the UI
      onUpdate()
      
    } catch (error) {
      alert('Failed to move aircraft. Please try again.')
    } finally {
      setIsMoving(false)
    }
  }

  const handleCloseMovementModal = () => {
    setShowMovementModal(false)
    setMovementToContainer(null)
    setMovementValidation(null)
  }

  const availableContainers = getAvailableTargetContainers()

  if (availableContainers.length === 0) {
    return null // No movement options available
  }

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={isValidating || isMoving}
          className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-md transition-colors duration-150 bg-blue-100 text-blue-800 hover:bg-blue-200 ${
            (isValidating || isMoving) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          title="Move aircraft between containers"
        >
          {isValidating ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Validating...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Move Aircraft
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          )}
        </button>

        {/* Dropdown menu */}
        {showDropdown && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200">
            <div className="py-1">
              <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
                Currently in: {getCurrentContainerDisplayName()}
              </div>
              {availableContainers.map((targetContainer) => (
                <button
                  key={targetContainer}
                  onClick={() => handleMoveAircraft(targetContainer)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                  disabled={isValidating || isMoving}
                >
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Move to {getContainerDisplayName(targetContainer)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Click outside to close dropdown */}
        {showDropdown && (
          <div 
            className="fixed inset-0 z-0" 
            onClick={() => setShowDropdown(false)}
          />
        )}
      </div>

      {/* Aircraft Movement Modal */}
      <AircraftMovementModal
        isOpen={showMovementModal}
        onClose={handleCloseMovementModal}
        onConfirm={handleConfirmMovement}
        aircraft={aircraft}
        fromContainer={currentContainer}
        toContainer={movementToContainer}
        validation={movementValidation}
        isLoading={isMoving}
      />
    </>
  )
}
